/* Capacity & Storage admin panel (iter-20).
 *
 * Shows live Mongo footprint per collection + growth deltas over the last
 * 7/30/90 days + admin-tunable warn/critical thresholds + manual alert
 * trigger. Powered by `/api/admin/capacity/*` endpoints — each `/report`
 * call also takes & persists a daily snapshot (idempotent per UTC day).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, RefreshCw, HardDrive, AlertTriangle, ShieldCheck, BellRing, Loader2, Cpu, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader, Field } from './_shared';
import { cn } from '@/lib/utils';
import { ResponsiveContainer, AreaChart, Area, YAxis, Tooltip } from 'recharts';

const STATUS_STYLES = {
  ok:       { bg: 'bg-success/15', fg: 'text-success', label: 'Healthy' },
  warn:     { bg: 'bg-warning/15', fg: 'text-warning', label: 'Warning' },
  critical: { bg: 'bg-destructive/15', fg: 'text-destructive', label: 'Critical' },
};

function StatBlock({ label, value, unit, testid, sparkData }) {
  const hasSpark = Array.isArray(sparkData) && sparkData.length >= 2;
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="font-mono-stat text-[18px] font-semibold">{value}</span>
        {unit && <span className="text-[10.5px] text-muted-foreground">{unit}</span>}
      </div>
      {hasSpark && (
        <div className="mt-2 h-8" data-testid={`${testid}-sparkline`}>
          <ResponsiveContainer>
            <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <defs>
                <linearGradient id={`grad-${testid}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <YAxis hide domain={['dataMin', 'dataMax']} />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  fontSize: 10, padding: '4px 8px',
                }}
                formatter={(v) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), unit || '']}
                labelFormatter={(_, items) => items?.[0]?.payload?.d || ''}
              />
              <Area type="monotone" dataKey="v" stroke="hsl(var(--primary))"
                     strokeWidth={1.5} fill={`url(#grad-${testid})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function GrowthCell({ delta, unit = 'MB' }) {
  if (delta == null) {
    return <span className="text-[11px] text-muted-foreground">no data yet</span>;
  }
  const v = Number(delta);
  const cls = v > 0 ? 'text-warning' : v < 0 ? 'text-success' : 'text-muted-foreground';
  const sign = v > 0 ? '+' : '';
  return (
    <span className={cn('font-mono-stat text-[12px]', cls)}>
      {sign}{v.toLocaleString(undefined, { maximumFractionDigits: 2 })} {unit}
    </span>
  );
}

export default function CapacityPanel() {
  const [report, setReport] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [warnMb, setWarnMb] = useState(1024);
  const [criticalMb, setCriticalMb] = useState(4096);
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [alertEmail, setAlertEmail] = useState('');
  const [cpuHeadroomPct, setCpuHeadroomPct] = useState(75);
  const [alerts, setAlerts] = useState([]);

  const reload = async () => {
    setBusy(true);
    try {
      const [r, a, snaps] = await Promise.all([
        adminApi.capacityReport(),
        adminApi.capacityAlerts(20).catch(() => ({ alerts: [] })),
        adminApi.capacitySnapshots(90).catch(() => ({ snapshots: [] })),
      ]);
      setReport(r);
      setWarnMb(r.thresholds.warn_mb);
      setCriticalMb(r.thresholds.critical_mb);
      setAlertsEnabled(!!r.thresholds.alerts_enabled);
      setCpuHeadroomPct(r.thresholds.cpu_headroom_pct ?? 75);
      setAlerts(a.alerts || []);
      // Sort snapshots ascending by date for sparkline rendering
      const snapList = (snaps.snapshots || []).slice().sort((a, b) => a.date.localeCompare(b.date));
      setSnapshots(snapList);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load capacity report.');
    } finally { setBusy(false); }
  };

  useEffect(() => { reload(); }, []);

  const saveThresholds = async () => {
    if (criticalMb <= warnMb) {
      toast.error('Critical threshold must be greater than warn threshold.');
      return;
    }
    setSavingThresholds(true);
    try {
      await adminApi.saveCapacityThresholds({
        warn_mb: parseInt(warnMb, 10),
        critical_mb: parseInt(criticalMb, 10),
        alerts_enabled: !!alertsEnabled,
        alert_email: alertEmail || null,
        cpu_headroom_pct: parseInt(cpuHeadroomPct, 10) || 75,
      });
      toast.success('Thresholds saved.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSavingThresholds(false); }
  };

  const triggerAlert = async () => {
    try {
      const r = await adminApi.triggerCapacityAlertCheck();
      if (r.alert_sent) toast.success('Alert email sent.');
      else toast.message('No alert needed (status is OK or already alerted today).');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Alert check failed.');
    }
  };

  const status = report?.status || 'ok';
  const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.ok;

  const totalStoragePct = useMemo(() => {
    if (!report) return 0;
    const cap = report.thresholds?.critical_mb || 1;
    return Math.min(100, Math.round((report.totals.storage_mb / cap) * 100));
  }, [report]);

  if (!report) {
    return (
      <section>
        <PanelHeader title="Capacity & Storage" />
        <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading capacity report…
        </div>
      </section>
    );
  }

  return (
    <section data-testid="admin-capacity-panel">
      <PanelHeader title="Capacity & Storage" />
      <p className="mb-4 text-[11.5px] text-muted-foreground">
        Live Mongo footprint per collection plus 7/30/90-day growth deltas. The deployment is
        considered <b>healthy</b> below the warn threshold, <b>warning</b> between warn and critical,
        and <b>critical</b> at or above critical. Toggle alert emails to be notified the moment a
        threshold is crossed.
      </p>

      {/* Status banner */}
      <div className={cn('mb-4 flex items-center gap-3 rounded-md border border-border p-3', statusStyle.bg)} data-testid="capacity-status-banner">
        {status === 'ok'       ? <ShieldCheck className={cn('h-5 w-5', statusStyle.fg)} /> :
         status === 'warn'     ? <AlertTriangle className={cn('h-5 w-5', statusStyle.fg)} /> :
                                  <AlertTriangle className={cn('h-5 w-5', statusStyle.fg)} />}
        <div className="flex-1">
          <div className={cn('text-[12.5px] font-semibold', statusStyle.fg)} data-testid="capacity-status-label">
            {statusStyle.label}
          </div>
          <div className="text-[11px] text-muted-foreground">
            Using <b className="font-mono-stat">{report.totals.storage_mb}</b> MB
            {' '}of <b className="font-mono-stat">{report.thresholds.critical_mb}</b> MB critical
            {' '}({totalStoragePct}%). Warn at <b className="font-mono-stat">{report.thresholds.warn_mb}</b> MB.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={reload} disabled={busy} className="gap-1.5" data-testid="capacity-refresh">
          <RefreshCw className={cn('h-3.5 w-3.5', busy && 'animate-spin')} /> Refresh
        </Button>
      </div>

      {/* Top-line totals + sparklines (iter-21) */}
      <div className="mb-5 grid grid-cols-2 gap-2 md:grid-cols-4">
        <StatBlock label="Storage" value={report.totals.storage_mb} unit="MB" testid="capacity-total-storage"
                    sparkData={snapshots.map(s => ({ d: s.date, v: (s.db_stats?.storage_bytes || 0) / (1024*1024) }))} />
        <StatBlock label="Data (uncompressed)" value={report.totals.data_mb} unit="MB" testid="capacity-total-data"
                    sparkData={snapshots.map(s => ({ d: s.date, v: (s.db_stats?.data_bytes || 0) / (1024*1024) }))} />
        <StatBlock label="Indexes" value={report.totals.index_mb} unit="MB" testid="capacity-total-indexes"
                    sparkData={snapshots.map(s => ({ d: s.date, v: (s.db_stats?.index_bytes || 0) / (1024*1024) }))} />
        <StatBlock label="Total documents" value={report.totals.objects.toLocaleString()} testid="capacity-total-objects"
                    sparkData={snapshots.map(s => ({ d: s.date, v: s.db_stats?.objects || 0 }))} />
      </div>

      {/* Growth deltas */}
      <div className="mb-5 rounded-md border border-border bg-card p-4">
        <div className="mb-2 text-[12.5px] font-semibold">Growth</div>
        <div className="grid grid-cols-3 gap-3 text-[11.5px]">
          {['7d', '30d', '90d'].map((k) => (
            <div key={k} className="rounded-sm border border-border/60 bg-panel-header/30 p-2" data-testid={`capacity-growth-${k}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k} delta</div>
              <div className="mt-1 flex flex-col gap-0.5">
                <div className="flex justify-between"><span>Storage</span><GrowthCell delta={report.growth[k]?.storage_mb_delta} /></div>
                <div className="flex justify-between"><span>Data</span><GrowthCell delta={report.growth[k]?.data_mb_delta} /></div>
                <div className="flex justify-between">
                  <span>Documents</span>
                  <GrowthCell delta={report.growth[k]?.objects_delta != null ? report.growth[k].objects_delta : null} unit="" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[10.5px] text-muted-foreground">
          Growth deltas use daily snapshots. New deployments populate snapshots automatically as you visit this page.
        </p>
      </div>

      {/* Per-collection table */}
      <div className="mb-5 rounded-md border border-border bg-card">
        <div className="border-b border-border px-4 py-2 text-[12.5px] font-semibold">Per-collection footprint</div>
        <div className="max-h-80 overflow-auto scroll-thin">
          <table className="w-full text-[11.5px]" data-testid="capacity-collections-table">
            <thead className="sticky top-0 bg-panel-header/60 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Collection</th>
                <th className="px-3 py-2 text-right font-medium">Rows</th>
                <th className="px-3 py-2 text-right font-medium">Storage</th>
                <th className="px-3 py-2 text-right font-medium">Indexes</th>
                <th className="px-3 py-2 text-right font-medium">Avg row</th>
              </tr>
            </thead>
            <tbody>
              {report.collections.map((c) => (
                <tr key={c.name} className="border-t border-border" data-testid={`capacity-row-${c.name}`}>
                  <td className="px-3 py-1.5">
                    {c.name === '_other'
                      ? <span className="italic text-muted-foreground">Other collections</span>
                      : <span className="font-mono-stat">{c.name}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{c.count.toLocaleString()}</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{c.storage_mb} MB</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{c.index_mb} MB</td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{c.avg_doc_size ? `${c.avg_doc_size} B` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Thresholds & alerts */}
      <div className="mb-5 rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="capacity-thresholds-card">
        <div className="mb-1 flex items-center gap-2 text-[12.5px] font-semibold">
          <HardDrive className="h-3.5 w-3.5 text-primary" /> Thresholds &amp; alerts
        </div>
        <p className="mb-3 text-[11px] text-muted-foreground">
          Set the storage budget for this deployment. When alerts are enabled and a Resend key is configured,
          the admin email below receives a one-shot notification each time the deployment crosses the warn
          or critical line (deduplicated by UTC date).
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Warn threshold (MB)">
            <Input type="number" min={1} value={warnMb}
                    onChange={(e) => setWarnMb(e.target.value)}
                    className="h-8 font-mono-stat text-[12px]"
                    data-testid="capacity-warn-mb" />
          </Field>
          <Field label="Critical threshold (MB)">
            <Input type="number" min={2} value={criticalMb}
                    onChange={(e) => setCriticalMb(e.target.value)}
                    className="h-8 font-mono-stat text-[12px]"
                    data-testid="capacity-critical-mb" />
          </Field>
          <Field label="Alert email">
            <Input value={alertEmail} onChange={(e) => setAlertEmail(e.target.value)}
                    placeholder="ops@yourdomain.com"
                    className="h-8 text-[12px]"
                    data-testid="capacity-alert-email" />
          </Field>
          <Field label="CPU alert threshold (% of vCPUs)">
            <Input type="number" min={10} max={100} value={cpuHeadroomPct}
                    onChange={(e) => setCpuHeadroomPct(e.target.value)}
                    className="h-8 font-mono-stat text-[12px]"
                    data-testid="capacity-cpu-headroom-pct" />
          </Field>
          <label className="flex items-end gap-2 pb-1 text-[12px]">
            <input type="checkbox" checked={alertsEnabled}
                    onChange={(e) => setAlertsEnabled(e.target.checked)}
                    data-testid="capacity-alerts-enabled" />
            Send Resend email when crossing thresholds
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" onClick={saveThresholds} disabled={savingThresholds} className="gap-1.5" data-testid="capacity-save-thresholds">
            <Save className="h-3.5 w-3.5" /> Save thresholds
          </Button>
          <Button size="sm" variant="outline" onClick={triggerAlert} className="gap-1.5" data-testid="capacity-trigger-alert">
            <BellRing className="h-3.5 w-3.5" /> Check &amp; alert now
          </Button>
        </div>
      </div>

      {/* Past alerts */}
      {alerts.length > 0 && (
        <div className="rounded-md border border-border bg-card">
          <div className="border-b border-border px-4 py-2 text-[12.5px] font-semibold">Recent alerts</div>
          <table className="w-full text-[11.5px]" data-testid="capacity-alerts-table">
            <thead className="bg-panel-header/60 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Storage @ alert</th>
                <th className="px-3 py-2 text-left font-medium">Sent to</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={`${a.date}-${a.status}`} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono-stat">{a.date}</td>
                  <td className="px-3 py-1.5">
                    <span className={cn('rounded-sm px-1.5 py-0.5 text-[10px] font-medium',
                      (STATUS_STYLES[a.status] || STATUS_STYLES.ok).bg,
                      (STATUS_STYLES[a.status] || STATUS_STYLES.ok).fg)}>
                      {(STATUS_STYLES[a.status] || STATUS_STYLES.ok).label}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono-stat">{a.storage_mb} MB</td>
                  <td className="px-3 py-1.5 truncate text-[11px] text-muted-foreground">{a.to}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ComputePlannerSection />
    </section>
  );
}


// iter-53n++ pass 12 — Compute (vCPU) planner section.
// Runs POST /api/admin/capacity/plan and renders the report inline.
function ComputePlannerSection() {
  const [running, setRunning] = React.useState(false);
  const [report, setReport] = React.useState(null);
  const [targetRps, setTargetRps] = React.useState('25');
  const [mau, setMau] = React.useState('');
  const [duration, setDuration] = React.useState('10');
  const [concurrency, setConcurrency] = React.useState('8');
  const [util, setUtil] = React.useState('0.60');
  const [alertOnBreach, setAlertOnBreach] = React.useState(false);

  const runPlan = async () => {
    setRunning(true);
    try {
      const body = {
        duration: Number(duration) || 10,
        concurrency: Number(concurrency) || 8,
        target_utilisation: Number(util) || 0.60,
        alert_on_breach: !!alertOnBreach,
      };
      if (targetRps) body.target_rps = Number(targetRps);
      if (mau) body.monthly_active_users = Number(mau);
      const r = await adminApi.runCapacityPlan(body);
      setReport(r);
      if (r?.cpu_alert?.email_sent) {
        toast.success('Capacity plan ready. CPU alert email dispatched.');
      } else if (r?.cpu_alert?.is_breach && alertOnBreach && !r?.cpu_alert?.alerts_enabled) {
        toast.warning('Plan breaches CPU threshold — enable alerts in Thresholds section to email ops.');
      } else {
        toast.success('Capacity plan ready.');
      }
    } catch (e) {
      const s = e?.response?.status;
      if (s === 429) toast.error('A plan is already running — try again in a few seconds.');
      else toast.error(e?.response?.data?.detail || 'Capacity plan failed.');
    } finally { setRunning(false); }
  };

  const m = report?.measured;
  const p = report?.plan;
  const a = report?.cpu_alert;
  return (
    <div className="mt-8 rounded-md border border-border bg-card p-4" data-testid="capacity-compute-planner">
      <div className="mb-2 flex items-center gap-2">
        <Cpu className="h-4 w-4 text-primary" />
        <h3 className="text-[13px] font-semibold">Compute (vCPU) planner</h3>
      </div>
      <p className="mb-3 text-[11.5px] text-muted-foreground">
        Fires a short mixed-endpoint load test against this pod, samples backend CPU-time
        via <code>psutil</code>, and returns the recommended vCPU + uvicorn-worker count for a
        target request rate. Excludes LLM endpoints (network-bound, not CPU-bound). Rate-limited
        to one run per pod at a time.
      </p>

      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-5">
        <Field label="Target RPS">
          <Input value={targetRps} onChange={(e) => setTargetRps(e.target.value)}
                  data-testid="capacity-plan-rps" placeholder="e.g. 25" />
        </Field>
        <Field label="…or MAU">
          <Input value={mau} onChange={(e) => setMau(e.target.value)}
                  data-testid="capacity-plan-mau" placeholder="e.g. 10000" />
        </Field>
        <Field label="Duration (s)">
          <Input value={duration} onChange={(e) => setDuration(e.target.value)}
                  data-testid="capacity-plan-duration" />
        </Field>
        <Field label="Concurrency">
          <Input value={concurrency} onChange={(e) => setConcurrency(e.target.value)}
                  data-testid="capacity-plan-concurrency" />
        </Field>
        <Field label="Utilisation (0-1)">
          <Input value={util} onChange={(e) => setUtil(e.target.value)}
                  data-testid="capacity-plan-util" />
        </Field>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={runPlan} disabled={running} className="gap-1.5"
                data-testid="capacity-plan-run">
          {running
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Measuring…</>
            : <><PlayCircle className="h-3.5 w-3.5" /> Run capacity plan</>}
        </Button>
        <label className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
          <input type="checkbox" checked={alertOnBreach}
                  onChange={(e) => setAlertOnBreach(e.target.checked)}
                  data-testid="capacity-plan-alert-on-breach" />
          Email ops if plan breaches CPU threshold
        </label>
        <span className="text-[11px] text-muted-foreground">
          Takes {duration || 10}s of live traffic. Safe on preview + production.
        </span>
      </div>

      {report && (
        <div className="mt-4 space-y-3" data-testid="capacity-plan-report">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <StatBlock label="Achieved RPS"   value={m.achieved_rps}     unit="req/s"
                        testid="capacity-plan-achieved-rps" />
            <StatBlock label="CPU / request"  value={m.cpu_ms_per_req}   unit="ms of CPU"
                        testid="capacity-plan-cpu-per-req" />
            <StatBlock label="p95 latency"    value={m.p95_latency_ms}   unit="ms"
                        testid="capacity-plan-p95" />
            <StatBlock label="Avg cores used" value={m.avg_cores_used}   unit={`of ${report.host?.system_vcpus || '?'} vCPUs`}
                        testid="capacity-plan-cores-used" />
          </div>

          {a && (
            <div
              className={cn('rounded-md border p-3 text-[12px]',
                a.is_breach
                  ? 'border-destructive/50 bg-destructive/5'
                  : 'border-border bg-muted/30')}
              data-testid="capacity-plan-cpu-alert"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className={cn('h-3.5 w-3.5',
                    a.is_breach ? 'text-destructive' : 'text-muted-foreground')} />
                <span className="font-semibold">
                  CPU headroom: {a.current_pct}% of {report.host?.system_vcpus} vCPUs
                  {' '}(threshold {a.threshold_pct}%)
                </span>
                {a.is_breach && (
                  <span className="ml-2 rounded-sm bg-destructive/15 px-1.5 py-0.5 text-[10.5px] font-semibold text-destructive"
                          data-testid="capacity-plan-breach-badge">
                    BREACH
                  </span>
                )}
                {a.email_sent && (
                  <span className="ml-2 rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-emerald-900"
                          data-testid="capacity-plan-alert-sent">
                    email sent
                  </span>
                )}
              </div>
              {a.is_breach && !a.alerts_enabled && (
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Enable <b>Send Resend email when crossing thresholds</b> above and re-run
                  to email ops automatically.
                </div>
              )}
            </div>
          )}

          <div className="rounded-md border border-border p-3 text-[12px]" data-testid="capacity-plan-workload">
            <span className="text-muted-foreground">Workload class:</span>{' '}
            <span className={cn('font-semibold',
              m.bottleneck === 'cpu-bound' ? 'text-warning' : 'text-primary')}>
              {m.bottleneck.toUpperCase()}
            </span>
            {report.inferred_note && (
              <div className="mt-1 text-[11px] text-muted-foreground">{report.inferred_note}</div>
            )}
          </div>

          {p ? (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-[12.5px]"
                  data-testid="capacity-plan-recommendation">
              <div className="mb-2 font-semibold">
                Plan — sustain {p.target_rps} req/s with {p.safety_headroom_pct}% headroom
              </div>
              <div className="grid gap-1.5 md:grid-cols-2">
                <div>Recommended vCPUs (total): <b className="font-mono-stat text-[15px]" data-testid="capacity-plan-vcpus">{p.recommended_vcpu_total}</b></div>
                <div>Recommended uvicorn workers: <b className="font-mono-stat text-[15px]" data-testid="capacity-plan-workers">{p.recommended_uvicorn_workers}</b></div>
                <div className="text-muted-foreground">Raw cores at 100% util: {p.raw_cores_required}</div>
                <div className="text-muted-foreground">CPU-seconds per request: {p.cpu_seconds_per_request} ms</div>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                Formula: <code>vcpus = ceil(target_rps × cpu_seconds/req / utilisation + 1 [OS/frontend] + 0.5 [background jobs])</code>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border p-3 text-[11.5px] text-muted-foreground"
                  data-testid="capacity-plan-no-target">
              Add a target RPS or MAU above and re-run to see a vCPU recommendation.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
